import React, { useState, useEffect, ReactNode } from 'react';
import { ArrowRight, CircleQuestionMark } from 'lucide-react';
import { useAppSelector } from 'src/store';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import Modal from 'src/shared/components/modal/Modal';
import { CMSSpecies } from 'src/shared/types';
import Show from 'src/shared/components/show/Show';
import Loader from 'src/shared/components/loader/Loader';
import QuestionButtonHelp from 'src/shared/components/question-button-help/QuestionButtonHelp';
import DataTable from 'src/shared/components/data-table/DataTable';
import { DataTableColumns, DataTableState } from 'src/shared/components/data-table/dataTableTypes';
import { Badge } from 'src/shared/components/ui/badge';
import { PrimaryButton } from 'src/shared/components/button/Button';
import useBoolean from 'src/shared/hooks/useBoolean';
import cmsService from 'src/services/cms-service';
import { modalTablesMergeComparator } from 'src/app/portfolio/helpers/portfolioHelpers';
import SpeciesImage from 'src/app/portfolio/components/species-image/SpeciesImage';
import BookCallModal from 'src/app/portfolio/components/book-call-modal/BookCallModal';
import DetectionBadge, { DetectionBadgeType } from 'src/app/portfolio/components/detection-badge/DetectionBadge';
import { getCurrentPortfolioFilters } from 'src/app/portfolio/state/portfolioGeneralSelector';
import { TableUpsellSection } from 'src/app/portfolio/components/table-upsell-section/TableUpsellSection';
import { getConfidenceDetail } from 'src/shared/helpers/speciesConfidence';
import { useSpeciesDetail } from 'src/app/portfolio/hooks/useSpeciesDetail';

type SpeciesInfoModalProps = {
    onClose?: () => void;
    commonName: string;
    scientificName: string;
    siteCoverage: {
        percentCoverage: number;
        siteCount: number;
    };
    statusChip?: ReactNode;
};

const confidenceOrder: { [key: string]: number } = {
    high: 0,
    medium: 1,
    low: 2,
};

const tableColumns: DataTableColumns = [
    {
        columnId: 'siteName',
        title: 'Site name',
        width: '30%',
    },
    {
        columnId: 'confidence',
        title: 'Prediction Confidence',
        width: '20%',
        titleSlug: 'species-risk-site-confidence',
    },
    {
        columnId: 'nmSamples',
        title: 'NatureMetrics Samples',
        width: '25%',
        titleSlug: 'species-risk-site-nm-samples',
        renderer: ({ cellData }) => {
            if (typeof cellData !== 'string') {
                return null;
            }
            return (
                <DetectionBadge
                    type={cellData as DetectionBadgeType}
                    iconPosition={cellData === DetectionBadgeType.BookCall ? 'end' : 'start'}
                />
            );
        },
        columnBanner: {
            shouldShow: ({ rows, columnIndex }) => modalTablesMergeComparator(rows, columnIndex, DetectionBadgeType.BookCall),
            content: <TableUpsellSection type={DetectionBadgeType.BookCall} />,
        },
    },
    {
        columnId: 'uploadedData',
        title: 'Uploaded Sample Data',
        width: '25%',
        titleSlug: 'species-risk-site-uploaded-data',
        renderer: ({ cellData }) => {
            if (typeof cellData !== 'string') {
                return null;
            }
            return (
                <DetectionBadge
                    type={cellData as DetectionBadgeType}
                    iconPosition={cellData === DetectionBadgeType.UploadData ? 'end' : 'start'}
                />
            );
        },
        columnBanner: {
            shouldShow: ({ rows, columnIndex }) => modalTablesMergeComparator(rows, columnIndex, DetectionBadgeType.UploadData),
            content: <TableUpsellSection type={DetectionBadgeType.UploadData} />,
        },
    },
];

const SpeciesInfoModal = (props: SpeciesInfoModalProps) => {
    const { commonName, scientificName, siteCoverage, statusChip, onClose } = props;
    const { currentCustomerId } = useCustomer();
    const [speciesCmsDetails, setSpeciesCmsDetails] = useState<CMSSpecies | null | undefined>(null);
    const [tableState, setTableState] = useState<Partial<DataTableState>>({ fixedHeader: true, rowsPerPage: 10 });
    const [showBookCallModal, setShowBookCallModal] = useBoolean(false);
    const selectedFilters = useAppSelector(getCurrentPortfolioFilters);

    // Source-aware: the grid-backed species page mounts the same modal inside a
    // provider, and the two responses are normalised to one shape.
    const { data: currentData, isFetching } = useSpeciesDetail({
        customerId: currentCustomerId,
        scientificName: scientificName,
        ...(selectedFilters.length ? { filters: selectedFilters } : {}),
    });

    useEffect(() => {
        if (!currentData) {
            return;
        }

        const tableData: DataTableState['data'] = [];

        const speciesAssetsList = [...(currentData.assessments || [])];

        speciesAssetsList
            .sort((a, b) => {
                const aConfidence = a.predictionLevel.toLowerCase();
                const bConfidence = b.predictionLevel.toLowerCase();
                return (confidenceOrder[aConfidence] ?? 99) - (confidenceOrder[bConfidence] ?? 99);
            })
            .forEach(asset => {
                const siteName = (
                    <div className='flex flex-col'>
                        <div className='text-sm'>{asset.projectName}</div>
                        <div className='text-xs text-grey-80'>{asset.country}</div>
                    </div>
                );

                const confidenceBadge = (
                    <Badge className={getConfidenceDetail(asset.predictionLevel).colorClass}>
                        {getConfidenceDetail(asset.predictionLevel).label}
                    </Badge>
                );

                // Display 'Detected/Undetected' badge depending on whether species found in Uploaded data
                // Display 'Upload data' badge if there is no uploaded data
                let uploadedDataBadgeType = DetectionBadgeType.UploadData;
                if (asset.hasUploadedData) {
                    uploadedDataBadgeType = asset.foundInUploadedData ? DetectionBadgeType.Detected : DetectionBadgeType.Undetected;
                }

                // Display 'Detected/Undetected' badge depending on whether species found in NM Samples
                // Display 'Book a call' badge if there is no NM Samples data
                let nmSamplesBadgeType = DetectionBadgeType.BookCall;
                if (asset.hasEDNAData) {
                    nmSamplesBadgeType = asset.foundInEDNA ? DetectionBadgeType.Detected : DetectionBadgeType.Undetected;
                }

                tableData.push([siteName, confidenceBadge, nmSamplesBadgeType, uploadedDataBadgeType]);
            });

        setTableState(prev => ({
            ...prev,
            data: tableData,
        }));
    }, [currentData]);

    useEffect(() => {
        if (!scientificName) {
            return;
        }

        const fetchSpeciesDetails = async () => {
            // Get species description from CMS
            const details = await cmsService.getSpeciesByScientificName(scientificName, 'fields[0]=description');

            // set undefined to indicate loading is complete
            setSpeciesCmsDetails(details ? details : undefined);
        };

        if (!speciesCmsDetails) {
            fetchSpeciesDetails();
        }
    }, [scientificName]);

    const speciesImage = (
        <SpeciesImage scientificName={scientificName} alt={scientificName} width='232px' height='130px' className='rounded-lg' />
    );

    // show loader until CMS details are fetched
    const isLoading = speciesCmsDetails === null || isFetching;

    const tableProps = {
        state: tableState,
        columns: tableColumns,
        onStateChange: setTableState,
        emptyStateMessage: `No Species Found`,
        className: 'overflow-visible',
        tableClassName: 'h-full',
    };

    // Check table rows for the current page for any site with no eDNA data
    // If found, display upsell for booking a call next to species description.
    const rowsThisPage = (tableState.data || []).slice(
        ((tableState.currentPageNumber || 1) - 1) * (tableState.rowsPerPage || 10),
        ((tableState.currentPageNumber || 1) - 1) * (tableState.rowsPerPage || 10) + (tableState.rowsPerPage || 10)
    );
    const hasSiteWithNoEdnaData = rowsThisPage.some(row => row[2] === DetectionBadgeType.BookCall);

    return (
        <Modal dismissOnClickOutside={true} classNames={{ body: 'w-[860px] max-h-4/5 p-6' }} onClose={onClose}>
            <Show when={!isLoading} fallback={<Loader />}>
                <div className='flex flex-col gap-4'>
                    <div className='flex flex-row gap-4'>
                        {speciesImage}
                        <div className='flex flex-col justify-between'>
                            <div className='flex flex-col gap-1'>
                                {commonName && <div className='text-3xl text-primary'>{commonName}</div>}
                                {scientificName && <div className='text-base text-grey-80 italic'>({scientificName})</div>}
                            </div>
                            <div className='flex flex-row gap-8'>
                                <div className='flex flex-col gap-1 justify-center'>
                                    <div className='flex flex-col text-sm text-grey-80'>
                                        <div className='flex'>
                                            Status{' '}
                                            <QuestionButtonHelp
                                                icon={<CircleQuestionMark className='text-grey-80 h-4' />}
                                                type='api'
                                                placement='right'
                                                slug='species-risk-site-status'
                                                zIndex={1300}
                                            />
                                        </div>
                                    </div>
                                    <div className='flex flex-col justify-center'>{statusChip}</div>
                                </div>
                                <div className='flex flex-col gap-1 justify-center'>
                                    <div className='flex flex-col justify-center text-sm text-grey-80'>
                                        <div className='flex justify-center'>
                                            Est. Site Coverage{' '}
                                            <QuestionButtonHelp
                                                icon={<CircleQuestionMark className='text-grey-80 h-4' />}
                                                type='api'
                                                placement='right'
                                                slug='species-risk-site-coverage'
                                                zIndex={1300}
                                            />
                                        </div>
                                    </div>
                                    <div className='flex flex-row gap-1'>
                                        <div className='flex flex-col justify-center text-xl text-black'>
                                            {siteCoverage.percentCoverage}%
                                        </div>
                                        <div className='flex flex-col justify-center text-sm text-grey-80'>
                                            ({siteCoverage.siteCount} site{siteCoverage.siteCount > 1 ? 's' : ''})
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='border-t border-grey-20 h-[1px]'>&nbsp;</div>
                    <div className='flex flex-col gap-2'>
                        <div className='flex flex-col gap-1'>
                            <div className='text-xl text-black'>Site Presence Assessment</div>
                            <div className='text-sm text-grey-80'>
                                The likelihood of {commonName || scientificName} occurring at each site.
                            </div>
                        </div>

                        <DataTable {...tableProps} />
                    </div>
                    <div className='flex gap-4'>
                        {speciesCmsDetails?.description && (
                            <div
                                className={`flex flex-col gap-2 p-4 border border-grey-20 rounded-lg ${
                                    hasSiteWithNoEdnaData ? 'w-[90%]' : 'w-full'
                                }`}
                            >
                                <div className='text-xl'>Species Bio</div>
                                <div
                                    className='text-sm text-grey-80'
                                    dangerouslySetInnerHTML={{ __html: speciesCmsDetails?.description }}
                                ></div>
                            </div>
                        )}

                        {hasSiteWithNoEdnaData && (
                            <div
                                className={`flex flex-col ${
                                    speciesCmsDetails?.description ? 'grow-0' : 'grow'
                                } gap-2 p-4 bg-grey-0 border border-grey-20 rounded-lg text-sm h-[fit-content]`}
                            >
                                Increase your data quality with ground-truthed eDNA & bioacoustics species insights.
                                <PrimaryButton className='w-[120px]' onClick={setShowBookCallModal.on}>
                                    Book call <ArrowRight className='text-white' />
                                </PrimaryButton>
                                {showBookCallModal && <BookCallModal onClose={setShowBookCallModal.off} />}
                            </div>
                        )}
                    </div>
                </div>
            </Show>
        </Modal>
    );
};

export default SpeciesInfoModal;
